import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, CircularProgress, Button, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import { getDocumentContent } from '../../services/documentationService';

export default function DocumentationViewer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const docPath = searchParams.get('doc') || '';
  const backPath = searchParams.get('back') || '/';

  useEffect(() => {
    const fetchMarkdown = async () => {
      if (!docPath) {
        setError('No document specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // 1) Try backend/manifest-based loader
        let text = await getDocumentContent(docPath);

        // Heuristic: if backend returns full HTML shell instead of markdown, fall back to static files
        const looksLikeHtml = /<!doctype html/i.test(text) || /<html[\s>]/i.test(text);

        if (!text || looksLikeHtml) {
          const candidates = [`/docs/${docPath}`, `/${docPath}`];

          for (const url of candidates) {
            try {
              const resp = await fetch(url, { cache: 'no-store' });
              if (!resp.ok) continue;
              const candidateText = await resp.text();
              const candidateLooksHtml = /<!doctype html/i.test(candidateText) || /<html[\s>]/i.test(candidateText);
              if (!candidateLooksHtml && candidateText.trim().length > 0) {
                text = candidateText;
                break;
              }
            } catch {
              // try next candidate
            }
          }
        }

        if (!text) {
          throw new Error('Document content is empty or could not be loaded');
        }

        setMarkdown(text);
      } catch (err) {
        console.error('Error loading markdown:', err);
        setError(err instanceof Error ? err.message : 'Failed to load documentation');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, [docPath]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backPath)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Paper sx={{ p: 4, maxWidth: '900px', mx: 'auto' }}>
        <Box
          sx={{
            '& h1': {
              color: '#003366',
              fontWeight: 700,
              fontSize: '2rem',
              mb: 2,
              mt: 3,
              '&:first-of-type': { mt: 0 }
            },
            '& h2': {
              color: '#003366',
              fontWeight: 600,
              fontSize: '1.5rem',
              mb: 2,
              mt: 3,
              borderBottom: '2px solid #003366',
              pb: 1
            },
            '& h3': {
              color: '#003366',
              fontWeight: 600,
              fontSize: '1.25rem',
              mb: 1.5,
              mt: 2
            },
            '& p': {
              mb: 2,
              lineHeight: 1.7
            },
            '& ul, & ol': {
              mb: 2,
              pl: 3
            },
            '& li': {
              mb: 0.5
            },
            '& code': {
              bgcolor: '#f6f8fa',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.875em'
            },
            '& pre': {
              bgcolor: '#f6f8fa',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 2,
              '& code': {
                bgcolor: 'transparent',
                p: 0
              }
            },
            '& a': {
              color: '#0066cc',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& blockquote': {
              borderLeft: '4px solid #003366',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: '#666'
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mb: 2
            },
            '& th, & td': {
              border: '1px solid #ddd',
              p: 1,
              textAlign: 'left'
            },
            '& th': {
              bgcolor: '#f6f8fa',
              fontWeight: 600
            }
          }}
        >
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  );
}
